import React, { useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

/**
 * Fase 11.5 Sprint 1 — Item 2: Captura de leads en N0 negativos.
 *
 * Convierte usuarios que llegaron a un resultado `no_apto`, `out_of_scope` o
 * `datos_insuficientes` en leads nutribles.
 *
 * Props:
 *   bucket: "no_apto" | "out_of_scope" | "datos_insuficientes"
 *   caseId: opcional, se incluye en la fuente para trazabilidad
 *   defaultEmail: opcional (si el usuario está logueado, pre-llenar)
 */

const BUCKET_COPY = {
  no_apto: {
    title: "¿Quieres que te avisemos cuando podamos ayudarte?",
    body: "Tu caso necesita ajustes específicos. Si te suscribes, te contactaremos cuando "
      + "Procure pueda procesar este tipo de casos o cuando tengamos recomendaciones operativas.",
    cta: "Notificarme",
  },
  out_of_scope: {
    title: "Tu caso está fuera del alcance automatizado",
    body: "Procure aún no maneja APP, concesiones complejas, urgencia manifiesta ni obra de alta "
      + "cuantía de forma automatizada. Déjanos tu correo y te notificamos cuando esté disponible "
      + "o cuando podamos ofrecerte acompañamiento personalizado.",
    cta: "Notificarme",
  },
  datos_insuficientes: {
    title: "¿Quieres ayuda para completar tu intake?",
    body: "Faltan datos para una prevalidación confiable. Si lo prefieres, te enviamos por correo "
      + "una guía paso-a-paso para completar tu información correctamente.",
    cta: "Recibir guía",
  },
};

export default function N0LeadCapture({ bucket, caseId, defaultEmail }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const copy = BUCKET_COPY[bucket] || BUCKET_COPY.no_apto;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || email.length < 4 || !email.includes("@")) {
      toast.error("Ingresa un correo válido.");
      return;
    }
    if (!consent) {
      toast.error("Debes aceptar la política de tratamiento de datos.");
      return;
    }
    setSending(true);
    try {
      const fuente = `n0_${bucket}${caseId ? `_${caseId.slice(0, 8)}` : ""}`;
      await api.post("/leads", { email, fuente });
      setDone(true);
      toast.success("¡Gracias! Te avisaremos por correo.");
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || "No se pudo registrar.";
      // El backend tiene rate limit 5/60s/IP. Si llega aquí varias veces seguidas, mostramos:
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div
        className="border border-emerald-200 bg-emerald-50/60 p-5 mb-6"
        data-testid={`n0-lead-capture-${bucket}-done`}
      >
        <p className="text-sm text-emerald-900">
          ✓ Listo. Te avisaremos por correo cuando podamos ayudarte con este tipo de casos.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="border border-gray-300 bg-white p-5 mb-6 rounded-sm"
      data-testid={`n0-lead-capture-${bucket}`}
    >
      <h3 className="font-serif text-lg text-gray-900 mb-1">{copy.title}</h3>
      <p className="text-sm text-gray-700 leading-relaxed mb-4">{copy.body}</p>
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
        <div className="flex-1">
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
            Tu correo
          </label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu.correo@entidad.gov.co"
            required
            className="rounded-sm h-10"
            data-testid={`n0-lead-capture-${bucket}-email`}
          />
        </div>
        <Button
          type="submit"
          disabled={sending || !email || !consent}
          className="rounded-sm h-10 px-5"
          data-testid={`n0-lead-capture-${bucket}-submit`}
        >
          {sending ? "Enviando…" : copy.cta}
        </Button>
      </div>
      <label className="flex items-start gap-2 mt-3 text-xs text-gray-600">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5"
          data-testid={`n0-lead-capture-${bucket}-consent`}
        />
        <span>
          Acepto recibir correos puntuales relacionados con mi caso y la{" "}
          <a href="/politica-datos" className="underline text-primary" target="_blank" rel="noopener noreferrer">
            política de tratamiento de datos
          </a>{" "}
          (Ley 1581 de 2012). Puedo darme de baja cuando quiera.
        </span>
      </label>
    </form>
  );
}
